"use client";

import { useEffect, useState } from "react";
import { Menu, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-8 w-24 bg-muted animate-pulse" />
        <div className="h-8 w-24 bg-muted animate-pulse" />
        <div className="h-8 w-24 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center gap-2 px-3 py-2 font-mono text-[13px] uppercase tracking-[0.12em] text-foreground hover:bg-transparent focus:bg-transparent transition-colors duration-200">
                <span className="text-primary opacity-60 transition-all duration-200 group-hover:translate-x-1 group-hover:text-primary group-hover:opacity-100" aria-hidden="true">
                  &gt;
                </span>
                <span className="relative font-default">
                  {item.label.toUpperCase().replace(/\s+/g, "_")}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-200 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 mt-12">
        <div className="h-10 w-full bg-muted animate-pulse" />
        <div className="h-10 w-full bg-muted animate-pulse" />
        <div className="h-10 w-full bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col mt-12 relative z-10">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="group flex items-center gap-3 border-b border-border py-5 font-mono text-base uppercase tracking-[0.12em] text-foreground transition-colors duration-200 hover:text-primary">
          <span className="text-primary opacity-60 transition-all duration-200 group-hover:translate-x-1 group-hover:text-primary">
            &gt;
          </span>
          <span className="font-default">{item.label.toUpperCase().replace(/\s+/g, "_")}</span>
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1725" id="main-navigation" className={`sticky top-0 left-0 right-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-background"}`}>
      <header className="relative">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12">
          <div className="grid h-[72px] grid-cols-12 items-center gap-4">
            <div className="col-span-6 md:col-span-3 flex items-center">
              <Link to="/HomePage" className="inline-flex items-center" aria-label="Current Pulse Electric — Home">
                <WvcLogo className="h-7 w-auto" />
              </Link>
            </div>

            <div className="hidden md:flex col-span-6 items-center justify-center">
              <MenuProvider menu_id="2" className="header-menu">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>

            <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-4">
              <div className="hidden lg:inline-flex items-center gap-2 border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-secondary">
                <span aria-hidden="true" className="inline-block h-1.5 w-1.5 bg-primary animate-pulse" />
                <span>LIVE</span>
              </div>

              <Button asChild className="hidden md:inline-flex h-10 bg-primary px-5 font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-primary-foreground hover:bg-primary/90">
                <Link to="/LetsTalkPage">
                  <span>REQUEST_SERVICE</span>
                  <ArrowRight className="ml-1 h-3.5 w-3.5" strokeWidth={2} />
                </Link>
              </Button>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden h-10 w-10 border border-border text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Open menu">
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md bg-background text-foreground border-l border-border p-6 overflow-hidden">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                  <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10" style={{
                  backgroundImage: "radial-gradient(circle, hsl(54 100% 50% / 0.5) 1px, transparent 1px)",
                  backgroundSize: "40px 40px"
                }} />

                  <div className="relative z-10 mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-secondary">
                    <span aria-hidden="true" className="inline-block h-1.5 w-1.5 bg-primary animate-pulse" />
                    <span>CPE_NAV_INDEX</span>
                  </div>

                  <MenuProvider menu_id="2" className="mobile-menu">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="relative z-10 mt-8 flex flex-col gap-4">
                    <Button asChild className="h-11 w-full bg-primary font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-primary-foreground hover:bg-primary/90">
                      <Link to="/LetsTalkPage" onClick={() => setMobileOpen(false)}>
                        <span>REQUEST_SERVICE</span>
                        <ArrowRight className="ml-1 h-3.5 w-3.5" strokeWidth={2} />
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        <div className="relative h-px w-full">
          <div className={`absolute inset-0 bg-border transition-all duration-300 ${scrolled ? "opacity-100" : "opacity-60"}`} />
        </div>
      </header>
    </section>;
}